import { useState, useRef, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = "Bare Property Shell",
  afterLabel = "Bloc & Brush Turnkey Finish",
  className = "",
}) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    let percentage = (x / rect.width) * 100;
    if (percentage < 5) percentage = 5;
    if (percentage > 95) percentage = 95;
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging) return;
      handleMove(e.touches[0].clientX);
    },
    [isDragging, handleMove]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    },
    [isDragging, handleMove]
  );

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden select-none cursor-ew-resize rounded-3xl shadow-[0_20px_50px_rgba(19,41,75,0.12)] border border-black/10 ${className}`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
    >
      {/* AFTER IMAGE (Full width background) */}
      <img
        src={afterImage}
        alt={afterLabel}
        className="w-full h-full object-cover pointer-events-none"
      />
      <div className="absolute top-6 right-6 bg-[#13294B]/90 backdrop-blur-md text-white text-xs font-semibold px-4 py-2 rounded-full tracking-wider uppercase z-10 border border-white/10 shadow-lg">
        {afterLabel}
      </div>

      {/* BEFORE IMAGE (Clipped on left side) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt={beforeLabel}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: containerRef.current?.getBoundingClientRect().width || "100%" }}
        />
        <div className="absolute top-6 left-6 bg-black/75 backdrop-blur-md text-white text-xs font-semibold px-4 py-2 rounded-full tracking-wider uppercase z-10 border border-white/10 shadow-lg">
          {beforeLabel}
        </div>
      </div>

      {/* SLIDER LINE & DRAG HANDLE */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_15px_rgba(0,0,0,0.5)] cursor-ew-resize z-20"
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 bg-white text-[#13294B] rounded-full flex items-center justify-center shadow-2xl border-2 border-[#13294B] transition-transform active:scale-95 hover:scale-105">
          <MoveHorizontal size={20} />
        </div>
      </div>
    </div>
  );
}
